export type NetworkKey = 'mainnet' | 'testnet';

export type DeploymentConfig = {
  factory: string;
  looker: string;
  adapter: string;
  oracle: string;
  collateral: string;
  gvr: string;
  registry: string;
};

export type PulseChainConfig = {
  id: number;
  chainIdHex: string;
  chainName: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
  rpcUrls: string[];
  blockExplorerUrls: string[];
  deploymentStartBlock: bigint;
  multicall3: string;
};

const NETWORK_STORAGE_KEY = 'boxbet:active-network';

export const PULSECHAIN_MAINNET: PulseChainConfig = {
  id: 369,
  chainIdHex: '0x171',
  chainName: 'PulseChain Mainnet',
  nativeCurrency: { name: 'Pulse', symbol: 'PLS', decimals: 18 },
  rpcUrls: [
    'https://rpc-pulsechain.g4mm4.io',
    'https://rpc.pulsechainstats.com',
    'https://pulsechain-rpc.publicnode.com',
    'https://rpc.pulsechain.com',
  ],
  blockExplorerUrls: ['https://scan.pulsechain.com'],
  deploymentStartBlock: 26822774n,
  multicall3: '0xcA11bde05977b3631167028862bE2a173976CA11',
};

export const PULSECHAIN_TESTNET: PulseChainConfig = {
  id: 943,
  chainIdHex: '0x3AF',
  chainName: 'PulseChain Testnet V4',
  nativeCurrency: { name: 'Pulse', symbol: 'tPLS', decimals: 18 },
  rpcUrls: ['https://rpc.v4.testnet.pulsechain.com'],
  blockExplorerUrls: ['https://scan.v4.testnet.pulsechain.com'],
  deploymentStartBlock: 24698088n,
  multicall3: '0xcA11bde05977b3631167028862bE2a173976CA11',
};

const DEPLOYMENTS: Record<NetworkKey, DeploymentConfig> = {
  mainnet: {
    factory: '0xeC97681F256Fa944Db475b6F34B8E317bd282DF5',
    looker: '0xbF48a614F83fB2e103aA32b8A80014c7c82672Fc',
    adapter: '0xb7EE86114dEda31568c6f4AE35DcA53726620b48',
    oracle: '0x0000000000000000000000000000000000000000',
    collateral: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07',
    gvr: '0x0000000000000000000000000000000000000000',
    registry: '0x0000000000000000000000000000000000000000',
  },
  testnet: {
    factory: '0xAB38b05B62155811e82EebB17F3F3f8aD30f78A1',
    looker: '0x99DF717466B3CCbce265f60097482390F87cAC1C',
    adapter: '0x248E0AA191AcEC1e64F2bc1a12086D9101F24dd6',
    oracle: '0x0000000000000000000000000000000000000000',
    collateral: '0x7007f459D5334b35B7A56147915cb08765d4CDC4',
    gvr: '0x0000000000000000000000000000000000000000',
    registry: '0x0000000000000000000000000000000000000000',
  },
};

const CHAINS: Record<NetworkKey, PulseChainConfig> = {
  mainnet: PULSECHAIN_MAINNET,
  testnet: PULSECHAIN_TESTNET,
};

function readStoredNetwork(): NetworkKey | null {
  if (typeof window === 'undefined') return null;
  const value = window.localStorage.getItem(NETWORK_STORAGE_KEY);
  return value === 'mainnet' || value === 'testnet' ? value : null;
}

let activeNetwork: NetworkKey = readStoredNetwork() ?? 'mainnet';

export let ACTIVE_CHAIN: PulseChainConfig = CHAINS[activeNetwork];
export let DEPLOYMENT: DeploymentConfig = DEPLOYMENTS[activeNetwork];

export function getActiveNetworkKey(): NetworkKey {
  return activeNetwork;
}

export function getActiveChainConfig(): PulseChainConfig {
  return ACTIVE_CHAIN;
}

export function setActiveNetwork(next: NetworkKey): void {
  activeNetwork = next;
  ACTIVE_CHAIN = CHAINS[next];
  DEPLOYMENT = DEPLOYMENTS[next];
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(NETWORK_STORAGE_KEY, next);
  }
}

export const OUTCOME_LABEL: Record<number, string> = {
  0: 'Unresolved',
  1: 'Yes',
  2: 'No',
  3: 'Invalid',
};

/**
 * UniswapV2-compatible router addresses used for on-chain token → USDC price quotes.
 * Tried in order; first successful getAmountsOut wins.
 */
export const NETWORK_ROUTERS: Record<NetworkKey, string[]> = {
  testnet: [
    '0x590FA712f49C6FdD621626f1B0473a04Aaa5c786', // PulseXRouter02 testnet
  ],
  mainnet: [
    '0x2221EEa96821E537F100C711dE439F79451c6A01', // DexTopRouter mainnet
    '0xaf5e33cb31A3454C950bee39ed1C76fd65b394cf', // PulseX router mainnet
  ],
};

/**
 * Canonical USDC address per network — used as the output leg of router quotes.
 */
export const NETWORK_USDC: Record<NetworkKey, string> = {
  testnet: '0x7007f459D5334b35B7A56147915cb08765d4CDC4',
  mainnet: '0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07',
};

/**
 * Lowercase token addresses that should be treated as 1:1 USD stablecoins.
 * Add any DAI or other peg-stable addresses here.
 */
export const NETWORK_STABLE_ADDRESSES: Record<NetworkKey, Set<string>> = {
  testnet: new Set([
    '0x7007f459d5334b35b7a56147915cb08765d4cdc4', // tUSDC testnet
    '0x826e4e896cc2f5b371cd7bb0bd929db3e3db67c0', // tDAI testnet
  ]),
  mainnet: new Set([
    '0x15d38573d2feeb82e7ad5187ab8c1d52810b1f07', // USDC mainnet
    '0xefd766ccb38eaf1dfd701853bfce31359239f305', // DAI-FETH (PulseChain DAI) mainnet
  ]),
};
